import React, { useState, useEffect } from 'react';
import { ElectionService, VoteService, VoteGroupService, MemberService } from '../services/firebaseService';
import { Election, Vote, VoteGroup, Member } from '../types';
import CandidatePhoto from '../components/CandidatePhoto';
import { 
  ChartBarIcon, 
  UsersIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CalendarIcon,
  TrophyIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const AdminResults: React.FC = () => {
  const [elections, setElections] = useState<Election[]>([]);
  const [votes, setVotes] = useState<Vote[]>([]);
  const [voteGroups, setVoteGroups] = useState<VoteGroup[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedElection, setSelectedElection] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [viewMode, setViewMode] = useState<'election' | 'group'>('election');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // États pour la réinitialisation des votes
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetType, setResetType] = useState<'election' | 'group' | 'all'>('election');
  const [resetTarget, setResetTarget] = useState<string>('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [electionsData, votesData, groupsData, membersData] = await Promise.all([
          ElectionService.getAllElections(),
          VoteService.getAllVotes(),
          VoteGroupService.getAllVoteGroups(),
          MemberService.getAllMembers()
        ]);
        
        setElections(electionsData);
        setVotes(votesData);
        setVoteGroups(groupsData);
        setMembers(membersData);
        
        if (electionsData.length > 0) {
          setSelectedElection(electionsData[0].id);
        }
        if (groupsData.length > 0) {
          setSelectedGroup(groupsData[0].id);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const selectedElectionData = elections.find(e => e.id === selectedElection);
  const selectedGroupData = voteGroups.find(g => g.id === selectedGroup);
  
  // Votes pour l'élection sélectionnée
  const electionVotes = votes.filter(v => v.electionId === selectedElection);
  const totalVotes = electionVotes.length;
  const abstentions = electionVotes.filter(v => v.isAbstention).length;
  const validVotes = totalVotes - abstentions;
  
  // Votes pour le groupe sélectionné
  const groupVotes = votes.filter(v => v.groupId === selectedGroup);
  const uniqueVotersInGroup = new Set(groupVotes.map(v => v.memberId)).size;
  const totalMembers = members.filter(m => m.hasVotingRights && m.membershipStatus === 'active').length;

  // Fonctions de réinitialisation des votes
  const handleResetVotes = async () => {
    setResetLoading(true);
    try {
      let deletedCount = 0;
      
      if (resetType === 'election' && resetTarget) {
        deletedCount = await VoteService.deleteVotesByElection(resetTarget);
        alert(`${deletedCount} vote(s) supprimé(s) pour cette élection`);
      } else if (resetType === 'group' && resetTarget) {
        deletedCount = await VoteService.deleteVotesByGroup(resetTarget);
        alert(`${deletedCount} vote(s) supprimé(s) pour ce groupe`);
      } else if (resetType === 'all') {
        deletedCount = await VoteService.deleteAllVotes();
        alert(`${deletedCount} vote(s) supprimé(s) au total`);
      }
      
      // Recharger les votes
      const votesData = await VoteService.getAllVotes();
      setVotes(votesData);
      setShowResetConfirm(false);
    } catch (error) {
      console.error('Erreur lors de la réinitialisation des votes:', error);
      alert('Erreur lors de la réinitialisation des votes');
    } finally {
      setResetLoading(false);
    }
  };

  const openResetConfirm = (type: 'election' | 'group' | 'all', target: string = '') => {
    setResetType(type);
    setResetTarget(target);
    setShowResetConfirm(true);
  };

  const getCandidateResults = () => {
    if (!selectedElectionData) return [];
    
    return selectedElectionData.candidates.map(candidate => {
      const candidateVotes = electionVotes.filter(v => v.candidateId === candidate.id).length;
      const percentage = totalVotes > 0 ? (candidateVotes / totalVotes) * 100 : 0;
      
      return {
        candidate,
        votes: candidateVotes,
        percentage
      };
    }).sort((a, b) => b.votes - a.votes);
  };

  const getGroupResults = () => {
    if (!selectedGroupData) return [];
    
    return selectedGroupData.elections.map(electionId => {
      const election = elections.find(e => e.id === electionId);
      if (!election) return null;
      
      const electionVotes = groupVotes.filter(v => v.electionId === electionId);
      const totalElectionVotes = electionVotes.length;
      const abstentions = electionVotes.filter(v => v.isAbstention).length;
      const validVotes = totalElectionVotes - abstentions;
      
      const candidateResults = election.candidates.map(candidate => {
        const candidateVotes = electionVotes.filter(v => v.candidateId === candidate.id).length;
        const percentage = totalElectionVotes > 0 ? (candidateVotes / totalElectionVotes) * 100 : 0;
        
        return {
          candidate,
          votes: candidateVotes,
          percentage
        };
      }).sort((a, b) => b.votes - a.votes);
      
      return {
        election,
        totalVotes: totalElectionVotes,
        abstentions,
        validVotes,
        candidateResults,
        winner: candidateResults.length > 0 ? candidateResults[0] : null
      };
    }).filter(Boolean);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Erreur</h3>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Résultats des Élections</h1>
        <p className="mt-2 text-gray-600">
          Consultez les résultats des élections et groupes de vote en temps réel
        </p>
      </div>

      {/* Mode de visualisation */}
      <div className="mb-6">
        <div className="flex space-x-4 mb-4">
          <button
            onClick={() => setViewMode('election')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              viewMode === 'election'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <ChartBarIcon className="w-5 h-5 inline mr-2" />
            Vue par élection
          </button>
          <button
            onClick={() => setViewMode('group')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              viewMode === 'group'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <UserGroupIcon className="w-5 h-5 inline mr-2" />
            Vue par groupe
          </button>
        </div>

        {/* Sélection selon le mode */}
        {viewMode === 'election' ? (
          <div className="flex items-end space-x-4">
            <div className="flex-1">
              <label htmlFor="election-select" className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner une élection
              </label>
              <select
                id="election-select"
                value={selectedElection}
                onChange={(e) => setSelectedElection(e.target.value)}
                className="input-field w-full"
              >
                {elections.map((election) => (
                  <option key={election.id} value={election.id}>
                    {election.title} - {election.position}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => openResetConfirm('election', selectedElection)}
              disabled={!selectedElection || electionVotes.length === 0}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Réinitialiser les votes
            </button>
          </div>
        ) : (
          <div className="flex items-end space-x-4">
            <div className="flex-1">
              <label htmlFor="group-select" className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner un groupe de vote
              </label>
              <select
                id="group-select"
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="input-field w-full"
              >
                {voteGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name} ({group.elections.length} élection{group.elections.length > 1 ? 's' : ''})
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => openResetConfirm('group', selectedGroup)}
              disabled={!selectedGroup || groupVotes.length === 0}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Réinitialiser les votes
            </button>
          </div>
        )}
      </div>

      {/* Contenu selon le mode de visualisation */}
      {viewMode === 'election' && selectedElectionData && (
        <div className="space-y-6">
          {/* Informations générales de l'élection */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {selectedElectionData.title}
            </h2>
            <p className="text-gray-600 mb-4">{selectedElectionData.description}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <UsersIcon className="h-8 w-8 text-blue-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-blue-600">Total des votes</p>
                    <p className="text-2xl font-semibold text-blue-900">{totalVotes}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-8 w-8 text-green-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-600">Votes valides</p>
                    <p className="text-2xl font-semibold text-green-900">{validVotes}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-yellow-600">Abstentions</p>
                    <p className="text-2xl font-semibold text-yellow-900">{abstentions}</p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <TrophyIcon className="h-8 w-8 text-purple-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-purple-600">Taux de participation</p>
                    <p className="text-2xl font-semibold text-purple-900">
                      {totalMembers > 0 ? ((totalVotes / totalMembers) * 100).toFixed(1) : 0}%
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Résultats par candidat */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Résultats par candidat</h3>
            
            {getCandidateResults().length > 0 ? (
              <div className="space-y-4">
                {getCandidateResults().map((result, index) => (
                  <div key={result.candidate.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className="text-lg font-bold text-gray-600 mr-3">#{index + 1}</span>
                        <CandidatePhoto 
                          photo={result.candidate.photo} 
                          name={result.candidate.name} 
                          size="md" 
                          className="mr-3"
                        />
                        <h4 className="text-lg font-medium text-gray-900">
                          {result.candidate.name}
                        </h4>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-primary-600">{result.votes}</p>
                        <p className="text-sm text-gray-500">votes</p>
                      </div>
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${result.percentage}%` }}
                      ></div>
                    </div>
                    
                    <p className="text-sm text-gray-500">
                      {result.percentage.toFixed(1)}% des votes totaux
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun vote enregistré</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Aucun vote n'a encore été enregistré pour cette élection.
                </p>
              </div>
            )}
          </div>

          {/* Statistiques détaillées */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistiques détaillées</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Période de vote</h4>
                <p className="text-sm text-gray-600">
                  {selectedElectionData.startDate.toLocaleDateString()} - {selectedElectionData.endDate.toLocaleDateString()}
                </p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Statut</h4>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  selectedElectionData.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {selectedElectionData.isActive ? 'Active' : 'Terminée'}
                </span>
              </div>
            </div>
          </div>

          {/* Détails des votes avec commentaires */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Détails des votes</h3>
            
            {getCandidateResults().length > 0 ? (
              <div className="space-y-6">
                {getCandidateResults().map((result) => {
                  const candidateVotes = electionVotes.filter(v => v.candidateId === result.candidate.id);
                  const hasComments = result.candidate.allowComments || false;
                  
                  return (
                    <div key={result.candidate.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <CandidatePhoto 
                          photo={result.candidate.photo} 
                          name={result.candidate.name} 
                          size="sm" 
                          className="mr-2"
                        />
                        <h4 className="text-md font-semibold text-gray-900">
                          {result.candidate.name}
                        </h4>
                        <span className="ml-auto text-sm text-gray-500">
                          {candidateVotes.length} vote{candidateVotes.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      
                      {candidateVotes.length > 0 ? (
                        <div className="space-y-3 mt-3">
                          {candidateVotes.map((vote, index) => {
                            const voter = members.find(m => m.id === vote.memberId);
                            const voterName = voter?.email || `Votant ${index + 1}`;
                            
                            return (
                              <div key={vote.id} className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                                <div className="flex items-start justify-between mb-2">
                                  <p className="text-sm font-medium text-gray-900">{voterName}</p>
                                  <span className="text-xs text-gray-500">
                                    {new Date(vote.timestamp || 0).toLocaleString()}
                                  </span>
                                </div>
                                
                                {hasComments && vote.comment && (
                                  <div className="mt-2 bg-white p-2 rounded border border-gray-200">
                                    <p className="text-xs font-medium text-gray-600 mb-1">
                                      {result.candidate.commentLabel || 'Commentaire'}:
                                    </p>
                                    <p className="text-sm text-gray-700 italic">"{vote.comment}"</p>
                                  </div>
                                )}
                                
                                {hasComments && !vote.comment && result.candidate.commentRequired && (
                                  <div className="mt-2 bg-yellow-50 p-2 rounded border border-yellow-200">
                                    <p className="text-xs text-yellow-700 italic">Aucun commentaire fourni (requis)</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">Aucun vote pour ce choix</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun vote enregistré</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Aucun vote n'a encore été enregistré pour cette élection.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vue par groupe */}
      {viewMode === 'group' && selectedGroupData && (
        <div className="space-y-6">
          {/* Informations générales du groupe */}
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {selectedGroupData.name}
            </h2>
            <p className="text-gray-600 mb-4">{selectedGroupData.description}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <UserGroupIcon className="h-8 w-8 text-blue-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-blue-600">Votants uniques</p>
                    <p className="text-2xl font-semibold text-blue-900">{uniqueVotersInGroup}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <UsersIcon className="h-8 w-8 text-green-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-600">Total des votes</p>
                    <p className="text-2xl font-semibold text-green-900">{groupVotes.length}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <TrophyIcon className="h-8 w-8 text-purple-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-purple-600">Taux de participation</p>
                    <p className="text-2xl font-semibold text-purple-900">
                      {totalMembers > 0 ? ((uniqueVotersInGroup / totalMembers) * 100).toFixed(1) : 0}%
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <CalendarIcon className="h-8 w-8 text-orange-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-orange-600">Élections</p>
                    <p className="text-2xl font-semibold text-orange-900">{selectedGroupData.elections.length}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Résultats par élection dans le groupe */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Résultats par élection</h3>
            
            {getGroupResults().length > 0 ? (
              <div className="space-y-6">
                {getGroupResults().map((result, index) => {
                  if (!result) return null;
                  
                  return (
                    <div key={result.election.id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-lg font-medium text-gray-900">
                            {result.election.title}
                          </h4>
                          <p className="text-sm text-gray-600">{result.election.position}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Votes: {result.totalVotes}</p>
                          <p className="text-sm text-gray-500">Abstentions: {result.abstentions}</p>
                        </div>
                      </div>

                      {/* Gagnant */}
                      {result.winner && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                          <div className="flex items-center">
                            <TrophyIcon className="h-6 w-6 text-yellow-600 mr-3" />
                            <div>
                              <p className="font-medium text-yellow-900">Gagnant: {result.winner.candidate.name}</p>
                              <p className="text-sm text-yellow-700">
                                {result.winner.votes} votes ({result.winner.percentage.toFixed(1)}%)
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Résultats des candidats */}
                      <div className="space-y-3">
                        {result.candidateResults.map((candidateResult, candidateIndex) => (
                          <div key={candidateResult.candidate.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center">
                              <span className="text-sm font-bold text-gray-600 mr-3">#{candidateIndex + 1}</span>
                              <CandidatePhoto 
                                photo={candidateResult.candidate.photo} 
                                name={candidateResult.candidate.name} 
                                size="sm" 
                                className="mr-3"
                              />
                              <span className="font-medium text-gray-900">{candidateResult.candidate.name}</span>
                            </div>
                            <div className="flex items-center space-x-4">
                              <div className="text-right">
                                <p className="text-lg font-bold text-gray-900">{candidateResult.votes}</p>
                                <p className="text-xs text-gray-500">votes</p>
                              </div>
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${candidateResult.percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-sm font-medium text-gray-600 w-12">
                                {candidateResult.percentage.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun vote enregistré</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Aucun vote n'a encore été enregistré pour ce groupe.
                </p>
              </div>
            )}
          </div>

          {/* Statistiques du groupe */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistiques du groupe</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Période de vote</h4>
                <p className="text-sm text-gray-600">
                  {selectedGroupData.startDate.toLocaleDateString()} - {selectedGroupData.endDate.toLocaleDateString()}
                </p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Statut</h4>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  selectedGroupData.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {selectedGroupData.isActive ? 'Actif' : 'Terminé'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale de confirmation de réinitialisation */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-800 bg-opacity-75 flex justify-center items-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Confirmer la réinitialisation
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                {resetType === 'election' && (
                  <>Êtes-vous sûr de vouloir supprimer tous les votes de cette élection ? Cette action est irréversible.</>
                )}
                {resetType === 'group' && (
                  <>Êtes-vous sûr de vouloir supprimer tous les votes de ce groupe d'élections ? Cette action est irréversible.</>
                )}
                {resetType === 'all' && (
                  <>Êtes-vous sûr de vouloir supprimer TOUS les votes ? Cette action est irréversible et supprimera tous les votes de toutes les élections.</>
                )}
              </p>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  disabled={resetLoading}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleResetVotes}
                  disabled={resetLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {resetLoading ? 'Suppression...' : 'Réinitialiser'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResults;
